<!--
  REV-21354 — the Setup-Wizard (Pure View). Redrawn to the prototype in REV-22096: a 720px card with
  the stepper in its head, a scrolling body and a footer of its own — the same three regions, the
  same measurements (`Vq` in aumico-awf-prototype-v3).

  Selector contract: tests bind to `data-testid` and `data-*` markers, never to styling classes and
  never to translated prose as an anchor. See e2e-lab/closing-setup-wizard.lab.spec.ts.

  Hand-rolled rather than `ham-modal`: that component moves itself into `#global-modal-placeholder`
  on init, which does not exist outside the app shell (`selectRootElement` throws when it is
  missing). A pure view must render inside its own subtree — the same reason `upload-modal` and
  `document-detail` hand-rolled theirs.
-->
<div
    class="wizard-modal"
    data-testid="closing-setup-wizard"
    role="dialog"
    aria-modal="true"
    [attr.aria-label]="'abcp.setup.hero.title' | i18next"
>
    <div class="wizard-modal__card">
        <div class="wizard">
            <!-- ================================ head: the stepper ================================ -->
            <!-- The container decides the entries by what it puts in `steps`. The connector is its own
                 element, like the prototype's, so it can turn green once the step before it is done. -->
            <ol class="wizard__stepper" *ngIf="steps.length > 1">
                <li
                    class="wizard__stepper-item"
                    *ngFor="let step of steps; let last = last; trackBy: trackByKey"
                    [class.wizard__stepper-item--last]="last"
                >
                    <span
                        class="wizard__stepper-pill"
                        [class.wizard__stepper-pill--active]="isActive(step)"
                        [class.wizard__stepper-pill--complete]="isComplete(step)"
                        [attr.aria-current]="isActive(step) ? 'step' : null"
                        data-testid="wizard-stepper-item"
                        [attr.data-step]="step.key"
                        [attr.data-active]="isActive(step)"
                        [attr.data-complete]="isComplete(step)"
                    >
                        <span class="wizard__stepper-badge" aria-hidden="true">
                            <lucide-icon *ngIf="isComplete(step)" name="check" [size]="12"></lucide-icon>
                            <ng-container *ngIf="!isComplete(step)">{{ displayNumber(step) }}</ng-container>
                        </span>
                        <span class="wizard__stepper-label">{{ step.label }}</span>
                    </span>
                    <span
                        class="wizard__stepper-line"
                        [class.wizard__stepper-line--complete]="isComplete(step)"
                        *ngIf="!last"
                        aria-hidden="true"
                    ></span>
                </li>
            </ol>

            <!-- ================================ body ============================================ -->
            <div class="wizard__body">
                <!-- ------------------------------ accounting ---------------------------------- -->
                <!-- Read-only: the ERP comes from the company's connection, and nothing is connected or
                     uploaded here. With an ERP, the slim tile and the years the start loads; without
                     one, the way into the settings and the file route as «Demnächst». -->
                <section
                    class="wizard__step"
                    *ngIf="isAccountingStep"
                    data-testid="wizard-accounting"
                    [attr.data-connected]="erp !== null"
                >
                    <h2 class="wizard__title">{{ 'abcp.setup.accounting.title' | i18next }}</h2>

                    <ng-container *ngIf="erp as connected; else noErp">
                        <div class="wizard__erp" data-testid="wizard-erp" [attr.data-managed-in]="connected.managedIn">
                            <span class="wizard__erp-banner" [style.background-color]="connected.bannerColor">
                                <img class="wizard__erp-logo" [src]="connected.logoSrc" [alt]="connected.name" />
                            </span>
                            <span class="wizard__erp-body">
                                <span class="wizard__erp-head">
                                    {{ connected.name }}
                                    <span class="wizard__erp-badge">{{
                                        'abcp.setup.accounting.connectedBadge' | i18next
                                    }}</span>
                                </span>
                                <span class="wizard__erp-meta">
                                    {{ connected.company }} ·
                                    <button
                                        type="button"
                                        class="wizard__link"
                                        data-testid="wizard-erp-manage"
                                        (click)="onManageErp()"
                                    >
                                        {{
                                            (connected.managedIn === 'organisation'
                                                ? 'abcp.setup.accounting.managedInOrg'
                                                : 'abcp.setup.accounting.managedInCompany'
                                            ) | i18next
                                        }}
                                    </button>
                                </span>
                            </span>
                        </div>

                        <div class="wizard__years" data-testid="wizard-years" *ngIf="years.length > 0">
                            <div class="wizard__panel-label">{{ 'abcp.setup.accounting.yearsTitle' | i18next }}</div>
                            <ul class="wizard__year-list">
                                <li
                                    class="wizard__year"
                                    data-testid="wizard-year"
                                    [attr.data-present]="year.present"
                                    *ngFor="let year of years; trackBy: trackByLine"
                                >
                                    <lucide-icon name="check" [size]="15" aria-hidden="true"></lucide-icon>
                                    <span>{{ year.label }}</span>
                                </li>
                            </ul>
                        </div>

                        <!-- The closing's currency, asked once here. Every amount is in it; nothing
                             derives it from the ERP. Required: «Weiter» waits for it. -->
                        <div class="wizard__currency" data-testid="wizard-currency">
                            <div class="wizard__panel-label">{{ 'abcp.setup.accounting.currencyTitle' | i18next }}</div>
                            <input
                                hamSimpleDropDown
                                [restoreSelectionOnBlur]="true"
                                name="currency"
                                data-testid="wizard-currency-input"
                                [options]="currencyOptions"
                                [optionKeys]="['label']"
                                returnKey="id"
                                [placeholder]="'abcp.setup.accounting.currencyPlaceholder' | i18next"
                                [ngModel]="currency"
                                (ngModelChange)="onCurrencyChange($event)"
                            />
                        </div>
                    </ng-container>

                    <ng-template #noErp>
                        <p class="wizard__lead">{{ 'abcp.setup.accounting.noErp' | i18next }}</p>

                        <div class="wizard__cards">
                            <button
                                type="button"
                                class="wizard__card wizard__card--link"
                                data-testid="wizard-card-api"
                                (click)="onSetupErp()"
                            >
                                <span class="wizard__card-head">
                                    <span class="wizard__card-icon" aria-hidden="true">
                                        <lucide-icon name="link" [size]="18"></lucide-icon>
                                    </span>
                                    <span class="wizard__card-title">{{
                                        'abcp.setup.accounting.apiTitle' | i18next
                                    }}</span>
                                </span>
                                <span class="wizard__card-desc"
                                    >{{ 'abcp.setup.accounting.setupInSettings' | i18next }} →</span
                                >
                            </button>

                            <div
                                class="wizard__card wizard__card--soon"
                                data-testid="wizard-card-file"
                                aria-disabled="true"
                            >
                                <span class="wizard__card-head">
                                    <span class="wizard__card-icon" aria-hidden="true">
                                        <lucide-icon name="file-text" [size]="18"></lucide-icon>
                                    </span>
                                    <span class="wizard__card-title">{{
                                        'abcp.setup.accounting.fileTitle' | i18next
                                    }}</span>
                                </span>
                                <span class="wizard__card-desc">{{ 'abcp.setup.accounting.fileDesc' | i18next }}</span>
                                <span class="wizard__soon">{{ 'abcp.setup.accounting.soon' | i18next }}</span>
                            </div>
                        </div>
                    </ng-template>
                </section>

                <!-- ------------------------------ documents ----------------------------------- -->
                <section class="wizard__step" *ngIf="isDocumentsStep">
                    <h2 class="wizard__title">{{ 'abcp.setup.documents.title' | i18next }}</h2>
                    <p class="wizard__lead">{{ 'abcp.setup.documents.lead' | i18next }}</p>

                    <!-- The SharePoint container goes here, inside the prototype's gray «Ordner
                         hinzufügen» panel. See the class comment: this view deliberately does not own a
                         second folder picker. -->
                    <div class="wizard__projected" data-testid="wizard-documents-slot">
                        <ng-content select="[documentsStep]"></ng-content>
                    </div>
                </section>

                <!-- Only for a closing that has already run once, on the screen that starts it. A first
                     launch has nothing to discard. -->
                <div
                    class="wizard__restart-warning"
                    data-testid="wizard-restart-warning"
                    *ngIf="isLastStep && alreadyStarted"
                    role="note"
                >
                    <lucide-icon name="triangle-alert" [size]="16" aria-hidden="true"></lucide-icon>
                    <span>{{ 'abcp.setup.review.restartWarning' | i18next }}</span>
                </div>
            </div>

            <!-- ================================ footer ========================================== -->
            <div class="wizard__footer">
                <ham-button data-testid="wizard-cancel" *ngIf="!hasPrevious" variant="white" (buttonClick)="onCancel()">
                    {{ 'abcp.setup.cancel' | i18next }}
                </ham-button>
                <ham-button
                    data-testid="wizard-back"
                    *ngIf="hasPrevious"
                    variant="white"
                    icon="assets/hamIcons/arrow-left-outline.svg"
                    (buttonClick)="onBack()"
                >
                    {{ 'abcp.setup.back' | i18next }}
                </ham-button>

                <ham-button
                    *ngIf="!isLastStep"
                    data-testid="wizard-next"
                    variant="primary"
                    rightIcon="assets/hamIcons/arrow-right.svg"
                    [disabled]="!canAdvance"
                    (buttonClick)="onNext()"
                >
                    {{ 'abcp.setup.next' | i18next }}
                </ham-button>

                <ham-button
                    *ngIf="isLastStep"
                    data-testid="wizard-start"
                    variant="primary"
                    lucideIcon="sparkles"
                    [disabled]="!canAdvance || starting"
                    (buttonClick)="onStart()"
                >
                    {{ 'abcp.setup.start.button' | i18next }}
                </ham-button>
            </div>
        </div>
    </div>
</div>
